import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getAvailableCurrencies } from '../../utils/currencyUtils';
import { buildAddRecentCurrencyPatch, getRecentCurrencyCodes } from '../../utils/userPreferencesHelpers';
import * as userActions from '../../store/actions/user';
import Layout from '../../components/Layout';
import FlagIcon from '../../components/FlagIcon';
import AddRecipientWizardShell, { WizardFormFooter } from '../../components/AddRecipientWizard';

function CurrencyFlagBadge({ currencyCode }) {
  return (
    <span
      className='flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-[var(--color-border)] bg-[var(--off)]'
      aria-hidden='true'
    >
      <FlagIcon currencyCode={currencyCode} className='h-4 w-5 rounded-sm' />
    </span>
  );
}

CurrencyFlagBadge.propTypes = {
  currencyCode: PropTypes.string.isRequired,
};

function CurrencyRow({ currency, onSelect, isSupported }) {
  const subtitle = [currency.countryName, currency.name].filter(Boolean).join(' · ') || currency.name;

  if (!isSupported) {
    return (
      <div
        className='flex w-full items-center gap-3.5 rounded-[10px] border border-transparent px-3.5 py-3 text-left opacity-60'
        aria-disabled='true'
      >
        <CurrencyFlagBadge currencyCode={currency.code} />
        <div className='min-w-0 flex-1'>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-sm font-medium text-[var(--n900)] font-[family-name:var(--font-ui)]'>{currency.code}</span>
            <span className='rounded-full bg-[var(--off)] px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
              Not available on your account
            </span>
          </div>
          <div className='mt-0.5 text-xs text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>{subtitle}</div>
        </div>
      </div>
    );
  }

  return (
    <button
      type='button'
      onClick={() => onSelect(currency.code)}
      className='flex w-full cursor-pointer items-center gap-3.5 rounded-[10px] border border-transparent px-3.5 py-3 text-left transition-colors hover:border-[var(--color-border)] hover:bg-[var(--off)]'
    >
      <CurrencyFlagBadge currencyCode={currency.code} />
      <div className='min-w-0 flex-1'>
        <div className='text-sm font-medium text-[var(--n900)] font-[family-name:var(--font-ui)]'>{currency.code}</div>
        <div className='mt-0.5 text-xs text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>{subtitle}</div>
      </div>
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='var(--text-tertiary)' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
        <path d='M9 18l6-6-6-6' />
      </svg>
    </button>
  );
}

CurrencyRow.propTypes = {
  currency: PropTypes.shape({
    code: PropTypes.string.isRequired,
    name: PropTypes.string,
    countryName: PropTypes.string,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
  isSupported: PropTypes.bool.isRequired,
};

function SelectCurrency() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState('');
  const { currencies: configCurrencies, countries: configCountries, supportedOffRampCurrencies } = useSelector((state) => state.config);
  const { preferences } = useSelector((state) => state.user);

  const currencies = useMemo(() => {
    if (!configCurrencies?.length) return [];
    return getAvailableCurrencies({ currencies: configCurrencies, countries: configCountries });
  }, [configCurrencies, configCountries]);

  const isSupported = (currencyCode) => {
    if (!Array.isArray(supportedOffRampCurrencies)) return true;
    if (!currencyCode) return false;
    return supportedOffRampCurrencies.includes(String(currencyCode).toUpperCase());
  };

  const sortBySupport = (list) =>
    [...list].sort((a, b) => {
      const aSupported = isSupported(a.code);
      const bSupported = isSupported(b.code);
      if (aSupported !== bSupported) return aSupported ? -1 : 1;
      return a.code.localeCompare(b.code);
    });

  const recentCurrencies = useMemo(() => {
    const recentCodes = getRecentCurrencyCodes(preferences);
    const list = recentCodes
      .map((code) => currencies.find((currency) => currency.code === code))
      .filter(Boolean);
    return sortBySupport(list);
  }, [preferences, currencies, supportedOffRampCurrencies]);

  const filteredCurrencies = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const list = !query
      ? currencies
      : currencies.filter((currency) => {
          if (currency.code.toLowerCase().includes(query) || currency.name.toLowerCase().includes(query)) {
            return true;
          }
          if (String(currency.countryName || '').toLowerCase().includes(query)) return true;
          if ((currency.countryNames || []).some((name) => String(name).toLowerCase().includes(query))) return true;
          if ((currency.countryCodes || []).some((code) => String(code).toLowerCase() === query)) return true;
          return false;
        });
    return sortBySupport(list);
  }, [currencies, searchQuery, supportedOffRampCurrencies]);

  const handleCurrencySelect = (currencyCode) => {
    if (!isSupported(currencyCode)) return;

    const patch = buildAddRecentCurrencyPatch(preferences, currencyCode);
    if (patch) {
      dispatch(userActions.upsertUserPreferences(patch));
    }
    navigate(`/recipient-type?currency=${currencyCode}`);
  };

  const handleCancel = () => {
    navigate('/recipients');
  };

  return (
    <Layout
      hideHeader
      mainPaddingClassName='px-6 py-9 sm:px-11'
      floatBack={{ onClick: handleCancel, label: 'Back', ariaLabel: 'Back to recipients' }}
    >
      <AddRecipientWizardShell
        currentStep={1}
        title='Select their currency'
        subtitle="Choose the currency your recipient will receive. We'll show the right payment rails next."
        footer={<WizardFormFooter onCancel={handleCancel} showContinue={false} />}
      >
        <div className='relative mb-[18px]'>
          <svg
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='var(--text-tertiary)'
            strokeWidth='1.8'
            className='pointer-events-none absolute left-4 top-1/2 -translate-y-1/2'
            aria-hidden='true'
          >
            <circle cx='11' cy='11' r='8' />
            <path d='M21 21l-4.35-4.35' />
          </svg>
          <input
            type='search'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder='Search currency or country'
            className='w-full rounded-lg border border-[var(--color-border)] bg-[var(--white)] py-3 pl-11 pr-3.5 text-[15px] text-[var(--n900)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--n600)] font-[family-name:var(--font-ui)]'
          />
        </div>

        {recentCurrencies.length > 0 && (
          <div className='mb-5'>
            <div className='mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
              Recent
            </div>
            <div className='flex flex-col gap-1'>
              {recentCurrencies.map((currency) => (
                <CurrencyRow
                  key={currency.code}
                  currency={currency}
                  onSelect={handleCurrencySelect}
                  isSupported={isSupported(currency.code)}
                />
              ))}
            </div>
          </div>
        )}

        <div>
          <div className='mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
            All currencies
          </div>
          {filteredCurrencies.length === 0 ? (
            <p className='py-8 text-center text-sm text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>No currencies match your search.</p>
          ) : (
            <div className='flex flex-col gap-1'>
              {filteredCurrencies.map((currency) => (
                <CurrencyRow
                  key={currency.code}
                  currency={currency}
                  onSelect={handleCurrencySelect}
                  isSupported={isSupported(currency.code)}
                />
              ))}
            </div>
          )}
        </div>
      </AddRecipientWizardShell>
    </Layout>
  );
}

export default SelectCurrency;
